'use client';

/**
 * SlackAvailabilityCallout
 *
 * Surfaces Meridian's Slack availability on the Hub /chat page (and any other
 * Meridian entry point that wants to opt in). Required by the universal rule
 * `.claude/rules/MERIDIAN_SLACK_AVAILABILITY.md` — Meridian is NOT a Hub-only
 * feature, it is also a full assistant in Slack (DM @Meridian, /meridian).
 *
 * Behavior:
 *  - Dismissible (X button). Dismissal persists in localStorage so it stays
 *    closed across page loads on the same browser.
 *  - Slack deep link prefers `slack://app?team=<TEAM_ID>&id=<APP_ID>` (opens
 *    the desktop app); falls back to `https://slack.com/app_redirect?app=<APP_ID>`.
 *  - IDs are resolved at runtime from props (preferred) or
 *    `NEXT_PUBLIC_SLACK_MERIDIAN_APP_ID` / `NEXT_PUBLIC_SLACK_MERIDIAN_TEAM_ID`
 *    public env vars. Never hardcoded — different deployments (dev/prod) may
 *    point at different Slack apps and workspaces.
 *
 * Required envs for the deep link to resolve (document in app `.env.example`):
 *   NEXT_PUBLIC_SLACK_MERIDIAN_APP_ID   = Slack app id, e.g. A0XXXXXXX
 *   NEXT_PUBLIC_SLACK_MERIDIAN_TEAM_ID  = Slack workspace/team id, e.g. T0XXXXXXX (optional)
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { MessageSquare, X } from 'lucide-react';

const DISMISSED_STORAGE_KEY = 'meridian.chat.slackCalloutDismissedV1';

export interface SlackAvailabilityCalloutProps {
  /** Override the resolved Slack app id (otherwise read from NEXT_PUBLIC_SLACK_MERIDIAN_APP_ID) */
  slackAppId?: string;
  /** Override the resolved Slack team/workspace id (otherwise read from NEXT_PUBLIC_SLACK_MERIDIAN_TEAM_ID) */
  slackTeamId?: string;
  /** Force-hide. Useful for hosts (Chrome extension) that should not surface this. */
  hidden?: boolean;
  /**
   * Variant. `banner` renders the dismissible bar shown at the top of the
   * chat area. `inline` renders a compact one-liner suitable for empty-state
   * embedding.
   */
  variant?: 'banner' | 'inline';
  /**
   * When true, also mention that Meridian can SEARCH Slack (messages + files).
   * Opt-in (default false) because only hosts with the Slack-search tools
   * wired (Hub) should advertise it — funnel chat surfaces should not.
   */
  mentionSearch?: boolean;
  /**
   * A surface-specific sentence appended to the callout — e.g. the routines
   * page saying that routines can be created and delivered from Slack too.
   * Keeps one Slack component (and one dismissal) instead of every Meridian
   * entry point growing its own Slack blurb.
   */
  note?: string;
}

function resolveSlackLink(appId: string | undefined, teamId: string | undefined): string | null {
  if (!appId) return null;
  if (teamId) return `slack://app?team=${encodeURIComponent(teamId)}&id=${encodeURIComponent(appId)}`;
  return `https://slack.com/app_redirect?app=${encodeURIComponent(appId)}`;
}

export function SlackAvailabilityCallout({
  slackAppId,
  slackTeamId,
  hidden = false,
  variant = 'banner',
  mentionSearch = false,
  note,
}: SlackAvailabilityCalloutProps) {
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState<boolean>(false);

  // Hydrate dismissed state from localStorage AFTER mount to avoid SSR/CSR
  // mismatch (localStorage is browser-only).
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
      if (stored === '1') setDismissed(true);
    } catch {
      // localStorage access can throw in private mode / sandboxed iframes — ignore.
    }
    setHydrated(true);
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, '1');
    } catch {
      // Ignore — dismissal still works for the session.
    }
  }, []);

  const appId =
    slackAppId ??
    (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_SLACK_MERIDIAN_APP_ID : undefined);
  const teamId =
    slackTeamId ??
    (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_SLACK_MERIDIAN_TEAM_ID : undefined);
  const slackHref = useMemo(() => resolveSlackLink(appId, teamId), [appId, teamId]);

  if (hidden) return null;
  // Don't render until hydration so SSR HTML matches client.
  if (!hydrated) return null;
  if (variant === 'banner' && dismissed) return null;

  if (variant === 'inline') {
    return (
      <p className="text-sm text-muted-foreground">
        <MessageSquare className="inline-block w-4 h-4 mr-1 align-text-bottom text-primary" aria-hidden />
        Meridian also lives in Slack — DM <code className="px-1 py-0.5 rounded bg-muted text-foreground">@Meridian</code> or use{' '}
        <code className="px-1 py-0.5 rounded bg-muted text-foreground">/meridian</code> in any channel.
        {mentionSearch ? ' It can also search your Slack messages & files — connect Slack to enable it.' : ''}
        {note ? ` ${note}` : ''}
        {slackHref ? (
          <>
            {' '}
            <a
              href={slackHref}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary hover:underline"
            >
              Open in Slack
            </a>
            .
          </>
        ) : null}
      </p>
    );
  }

  return (
    <div
      role="status"
      className="flex items-start gap-3 border-b border-border bg-primary/5 px-4 py-3 sm:px-6"
    >
      <MessageSquare className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
      <div className="flex-1 min-w-0 text-sm text-foreground">
        <p className="leading-snug">
          <span className="font-semibold">Meridian also lives in Slack.</span>{' '}
          DM <code className="px-1 py-0.5 rounded bg-muted text-foreground">@Meridian</code> or use{' '}
          <code className="px-1 py-0.5 rounded bg-muted text-foreground">/meridian</code> in any channel —
          same conversation, less tab-switching.
          {mentionSearch
            ? ' It can also search your Slack messages & files — connect Slack to enable full search.'
            : ''}
          {note ? ` ${note}` : ''}
        </p>
        {slackHref ? (
          <a
            href={slackHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-1 text-xs font-medium text-primary hover:underline focus:outline-none focus:underline"
          >
            Open Meridian in Slack →
          </a>
        ) : null}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss Slack availability notice"
        className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 -mr-2 -my-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <X className="w-4 h-4" aria-hidden />
      </button>
    </div>
  );
}
